import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import useOrg from '@/hooks/useOrg';
import { userPreferences as prefsApi } from '@/lib/trackizy';

const PREF_KEY = 'pinned_vehicles_v1';
const LS_KEY = 'trackizy_pinned_vehicles';
const MAX_PINNED = 50;
const DEBOUNCE_MS = 500;

/**
 * useFavoriteVehicles — manages a set of pinned/favorite vehicle IDs,
 * persisted to the server via user preferences API with localStorage fallback.
 */
export default function useFavoriteVehicles() {
  const org = useOrg();
  const user = useSelector((state) => state.session.user);

  const [pinnedIds, setPinnedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const debounceTimer = useRef(null);

  // ---- helpers for localStorage fallback ----
  const readLS = useCallback(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? new Set(parsed) : new Set();
    } catch {
      return new Set();
    }
  }, []);

  const writeLS = useCallback((ids) => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify([...ids]));
    } catch {
      /* quota exceeded — ignore */
    }
  }, []);

  // ---- persist to server (debounced) ----
  const persistToServer = useCallback(
    (ids) => {
      if (!org?.id || !user?.id) return;

      if (debounceTimer.current) clearTimeout(debounceTimer.current);

      debounceTimer.current = setTimeout(async () => {
        try {
          await prefsApi.set(PREF_KEY, { ids: [...ids] });
        } catch (err) {
          console.warn('[useFavoriteVehicles] save error:', err);
        }
      }, DEBOUNCE_MS);
    },
    [org?.id, user?.id],
  );

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  // ---- load on mount ----
  useEffect(() => {
    if (!org?.id || !user?.id) {
      setPinnedIds(readLS());
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const data = await prefsApi.get(PREF_KEY);

        if (cancelled) return;

        if (data?.value?.ids && Array.isArray(data.value.ids)) {
          setPinnedIds(new Set(data.value.ids));
        } else {
          // Fall back to localStorage if server has no data yet
          setPinnedIds(readLS());
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('[useFavoriteVehicles] load error:', err);
          setPinnedIds(readLS());
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [org?.id, user?.id, readLS]);

  // ---- isPinned ----
  const isPinned = useCallback(
    (deviceId) => pinnedIds.has(deviceId),
    [pinnedIds],
  );

  // ---- togglePin ----
  const togglePin = useCallback(
    (deviceId) => {
      setPinnedIds((prev) => {
        const next = new Set(prev);
        if (next.has(deviceId)) {
          next.delete(deviceId);
        } else {
          if (next.size >= MAX_PINNED) return prev; // cap reached
          next.add(deviceId);
        }

        // Always write to localStorage immediately
        writeLS(next);
        // Debounced server write
        persistToServer(next);

        return next;
      });
    },
    [writeLS, persistToServer],
  );

  // Stable Set reference via useMemo to avoid unnecessary re-renders for consumers
  const stablePinnedIds = useMemo(() => pinnedIds, [pinnedIds]);

  return {
    pinnedIds: stablePinnedIds,
    isPinned,
    togglePin,
    loading,
  };
}
